/**
 * REST API service for RAG (Retrieval Augmented Generation) chat with Legal, TEBG, ITSM
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccessTokenHelper } from './msalAuth';

export type RestRagType = 'Legal' | 'DevOps' | 'DataSecurity' | 'TEBG' | 'PPMO' | 'Travel & Expenses' | 'ITSM' | 'HR' | 'EverydayInsights' | 'Jira';


export interface RestRagResponseChunk {
  type: 'content' | 'citations' | 'done';
  data: {
    content: string;
  };
  contentType?: 'thinking' | 'answer'; // Reporting Portal/Data Security RAG emits thinking vs answer phases
  error_data: any | null;
  done: any | null;
  source: string;
  error?: any; // error field (DevOps/Legal/TEBG/Travel & Expenses)
  citations?: any[]; // citations array (DevOps/Legal/TEBG/Travel & Expenses)
  citation_count?: number; // citation count (DevOps/Legal/TEBG/Travel & Expenses)
  follow_ups?: string[]; // follow-up suggestion questions (Travel & Expenses)
}

interface RagEndpoint {
  path: string;
  name: string;
  useApim: boolean; // Whether to use APIM base URL or direct endpoint
}

// Get RAG-specific APIM base URL from environment variable with fallback
// RAG endpoints may be routed differently than main chat API
const RAG_APIM_BASE_URL = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';

const RAG_ENDPOINTS: Record<RestRagType, RagEndpoint> = {
  Legal: {
    path: '/nav-rag-api/la-rag',
    name: 'LA',
    useApim: true,
  },
  DevOps: {
    path: '/nav-rag-api/devops-rag',
    name: 'DevOps',
    useApim: true,
  },
  DataSecurity: {
    //path: 'https://np-navigator-rag-api-aaebfgdmdkdkaccd.eastus2-01.azurewebsites.net/data-services/query',
    path: '/nav-rag-api/data-services-rag',
    name: 'DataSecurity',
    //useApim: false, // Direct FastAPI endpoint
    useApim: true,
  },
  TEBG: {
    path: '/nav-rag-api/tebg-rag',
    name: 'TEBG',
    useApim: true,
  },
  PPMO: {
    path: '/nav-rag-api/ppmo-rag',
    name: 'PPMO',
    useApim: true,
  },
  'Travel & Expenses': {
    path: '/nav-rag-api/gte-rag',
    name: 'Travel & Expenses',
    useApim: true,
  },
  ITSM: {
    path: '/nav-rag-api/itsm-rag',
    name: 'ITSM',
    useApim: true,
  },
  HR: {
    path: '/nav-rag-api/hr-rag',
    name: 'HR',
    useApim: true,
  },
  EverydayInsights: {
    path: 'https://ai-np-apim-main.mmm.com/everydayinsights',
    name: 'EverydayInsights',
    useApim: false, // Uses its own APIM URL with a dedicated subscription key
  },
  Jira: {
    path: '/nav-rag-api/jira-rag',
    name: 'Jira',
    useApim: true
  }
};

/**
 * Get the full URL for a RAG endpoint
 */
const getRagEndpointUrl = (ragType: RestRagType): string => {
  const endpoint = RAG_ENDPOINTS[ragType];
  return endpoint.useApim ? `${RAG_APIM_BASE_URL}${endpoint.path}` : endpoint.path;
};

export class RestRagChatService {
  private ragType: RestRagType;
  private msalInstance: IPublicClientApplication;
  private abortController: AbortController | null = null;
  private onMessageCallback: ((chunk: RestRagResponseChunk) => void) | null = null;
  private onErrorCallback: ((error: Error) => void) | null = null;
  private onCompleteCallback: (() => void) | null = null;
  private onCompleteFired: boolean = false; // guard against double-firing (e.g. [DONE] + reader done)

  constructor(ragType: RestRagType, msalInstance: IPublicClientApplication) {
    this.ragType = ragType;
    this.msalInstance = msalInstance;
  }

  /**
   * Set callback for receiving message chunks
   */
  public onMessage(callback: (chunk: RestRagResponseChunk) => void): void {
    this.onMessageCallback = callback;
  }

  /**
   * Set callback for errors
   */
  public onError(callback: (error: Error) => void): void {
    this.onErrorCallback = callback;
  }

  /**
   * Set callback for completion
   */
  public onComplete(callback: () => void): void {
    this.onCompleteCallback = callback;
  }

  /**
   * Get the access token for API requests (delegates to shared helper)
   */
  private async getAccessToken(forceRefresh = false): Promise<string> {
    return getAccessTokenHelper(this.msalInstance, forceRefresh);
  }

  /**
   * Send a query to the RAG service
   * @param query - The user's query text
   * @param chatHistory - Optional full chat history for multi-turn conversations
   */
  public async sendQuery(query: string, chatHistory?: Array<{role: string; content: string}>): Promise<void> {
    return this.sendQueryInternal(query, chatHistory, false);
  }

  private async sendQueryInternal(query: string, chatHistory: Array<{role: string; content: string}> | undefined, isRetry: boolean): Promise<void> {
    const endpoint = RAG_ENDPOINTS[this.ragType];
    const endpointUrl = getRagEndpointUrl(this.ragType);
    
    // Reset guard so a new request can fire onComplete
    this.onCompleteFired = false;
    
    // Helper to fire onComplete exactly once per request
    const fireComplete = () => {
      if (!this.onCompleteFired) {
        this.onCompleteFired = true;
        if (this.onCompleteCallback) {
          this.onCompleteCallback();
        }
      }
    };
    
    try {
      // Create new abort controller for this request
      this.abortController = new AbortController();

      // Get access token for APIM authentication; force a fresh token on retry
      const accessToken = await this.getAccessToken(isRetry);

      // Build headers - add service-specific APIM headers if needed
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'Authorization': `Bearer ${accessToken}`,
      };

      // Build request body - DevOps requires additional fields
      // Use full chat history if provided, otherwise fall back to single message with current query
      const chatHistoryToSend = chatHistory || [
        {
          role: 'user',
          content: query
        }
      ];
      // TODO PPMO, DATASECURITY, ITSM
      const requestBody = (this.ragType === 'DevOps' || this.ragType === 'Legal' || this.ragType === 'EverydayInsights' || this.ragType === 'DataSecurity' || this.ragType === 'TEBG' || this.ragType === 'PPMO' || this.ragType === 'Travel & Expenses' || this.ragType === 'ITSM' || this.ragType === 'HR')
      // const requestBody = (this.ragType === 'DevOps' || this.ragType === 'Legal' || this.ragType === 'EverydayInsights' || this.ragType === 'TEBG' || this.ragType === 'Travel & Expenses')

        ? { 
            query,
            request_id: `${this.ragType.toLowerCase()}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
            timestamp: new Date().toISOString(),
            query_type: 'chat',
            chat_history: chatHistoryToSend
          }
        : { query };

      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      // Process the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          fireComplete();
          break;
        }

        // Decode the chunk
        buffer += decoder.decode(value, { stream: true });

        // Process complete Server-Sent Events (SSE) messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEventType = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            // Data line - parse and emit
            const dataStr = line.substring(5).trim();
            
            if (dataStr) {
              // Check for [DONE] marker (DevOps RAG uses this)
              if (dataStr === '[DONE]') {
                fireComplete();
                continue;
              }

              try {
                const parsedData = JSON.parse(dataStr);
                
                // Convert DevOps event format to standard chunk format
                let chunk: RestRagResponseChunk;
                
                // DevOps/DataSecurity sends streaming content as {"delta": {"content": "text", "type": "thinking"|"answer"}}
                if (parsedData.delta && parsedData.delta.content !== undefined) {
                  chunk = {
                    type: 'content',
                    data: {
                      content: parsedData.delta.content,
                    },
                    // Carry through the delta type for Reporting Portal/Data Security thinking/answer phases
                    contentType: parsedData.delta.type === 'thinking' ? 'thinking' : parsedData.delta.type === 'answer' ? 'answer' : undefined,
                    error_data: null,
                    done: null,
                    source: endpoint.name,
                  };
                }
                // DevOps completion with status: "done"
                else if (parsedData.status === 'done') {
                  chunk = {
                    type: 'done',
                    data: {
                      content: '',
                    },
                    error_data: null,
                    done: parsedData,
                    source: endpoint.name,
                  };
                }
                // DevOps citations
                else if (parsedData.citations && Array.isArray(parsedData.citations)) {
                  chunk = {
                    type: 'citations',
                    data: {
                      content: '',
                    },
                    error_data: null,
                    done: null,
                    source: endpoint.name,
                    ...parsedData,
                  };
                }
                // Filter out DevOps status updates (not content)
                else if (parsedData.status && ['retrieving', 'retrieved', 'reranking', 'reranked', 'generating'].includes(parsedData.status)) {
                  continue; // Skip status events
                }
                // Filter out DevOps retrieved chunk metadata (not streaming content)
                else if (parsedData.source && parsedData.content && parsedData.score !== undefined && parsedData.position !== undefined) {
                  continue; // Skip retrieved chunk metadata
                }
                else if (currentEventType === 'stream') {
                  // DevOps stream event - extract text content
                  const textContent = parsedData.content || parsedData.chunk || parsedData.text || '';
                  chunk = {
                    type: 'content',
                    data: {
                      content: textContent,
                    },
                    error_data: null,
                    done: null,
                    source: endpoint.name,
                  };
                } else if (currentEventType === 'complete') {
                  // DevOps complete event - convert to done chunk
                  chunk = {
                    type: 'done',
                    data: {
                      content: parsedData.response || '',
                    },
                    error_data: null,
                    done: parsedData,
                    source: endpoint.name,
                  };
                } else if (currentEventType === 'citations') {
                  // DevOps citations event - convert to citations chunk
                  chunk = {
                    type: 'citations',
                    data: {
                      content: '',
                    },
                    error_data: null,
                    done: null,
                    source: endpoint.name,
                    ...parsedData, // Include citations array and citation_count
                  };
                } else if (currentEventType === 'error') {
                  // DevOps error event
                  chunk = {
                    type: 'content',
                    data: {
                      content: '',
                    },
                    error_data: parsedData,
                    done: null,
                    source: endpoint.name,
                    error: parsedData.error,
                  };
                } else if (parsedData.type) {
                  // Standard format with explicit type field
                  chunk = parsedData;
                } else {
                  continue; // Unknown format - skip
                }

                // Emit the chunk to the callback
                if (this.onMessageCallback) {
                  this.onMessageCallback(chunk);
                }
                
                // Reset event type after processing
                currentEventType = '';
              } catch (parseError) {
                console.error('[REST RAG] Error parsing SSE data:', parseError);
              }
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        // Detect browser-level CORS / network failure (TypeError thrown before any HTTP
        // response is received — e.g. "Failed to fetch", "NetworkError when attempting
        // to fetch resource", "Load failed").
        const isCorsOrNetwork =
          error instanceof TypeError &&
          /(failed to fetch|networkerror|load failed)/i.test(error.message);
        // On a CORS/network failure, automatically retry once with a force-refreshed
        // token. This handles the case where a near-expired token was accepted by MSAL
        // but rejected by APIM, causing a CORS-less error response. This mirrors what
        // users achieve by manually refreshing the page.
        if (isCorsOrNetwork && !isRetry) {
          return this.sendQueryInternal(query, chatHistory, true);
        }
        const surfacedError = isCorsOrNetwork
          ? new Error(
              'Your request could not be completed. Please try again or try rephrasing your prompt. ' +
              'If this keeps happening, please contact support.'
            )
          : error;
        if (this.onErrorCallback) {
          this.onErrorCallback(surfacedError);
        }
        throw surfacedError;
      }
      throw error;
    }
  }

  /**
   * Cancel the current query
   */
  public cancel(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Cleanup resources
   */
  public dispose(): void {
    this.cancel();
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onCompleteCallback = null;
  }
}
